import axios from 'axios';

// Force HTTPS for production
const getBackendUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  // Always ensure HTTPS in production
  if (envUrl && envUrl.includes('http://')) {
    return envUrl.replace('http://', 'https://');
  }
  // Use window.location.origin if no env var or already HTTPS
  if (!envUrl || envUrl === 'undefined') {
    return window.location.origin;
  }
  return envUrl;
};

const BACKEND_URL = getBackendUrl();
const API_BASE = `${BACKEND_URL}/api`;

console.log('[API] Using backend URL:', BACKEND_URL);

// Create axios instance — withCredentials sends/receives the httpOnly auth
// cookies (access_token / session_token). Tokens are no longer stored in
// localStorage (XSS hardening).
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: legacy Bearer fallback + shadow id + HTTPS enforcement
api.interceptors.request.use(
  (config) => {
    // Legacy migration path: sessions created before the cookie switch still
    // have a localStorage JWT. Send it as Bearer until it expires; new logins
    // rely purely on the httpOnly cookie.
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Always attach the device shadow id so anonymous guests can perform
    // tournament write operations (organizer = shadow_id when no user).
    try {
      let shadowId = localStorage.getItem('loveall:shadow_id');
      if (!shadowId) {
        shadowId = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36));
        localStorage.setItem('loveall:shadow_id', shadowId);
      }
      config.headers['X-Shadow-Id'] = shadowId;
    } catch { /* localStorage unavailable — skip */ }
    // Attach the tournament scoring PIN (if any) for tournament write actions.
    // Pattern: /tournaments/{id}/matches/{mid}/start|score and other guarded routes.
    try {
      const url = config.url || '';
      const m = url.match(/\/tournaments\/([^/]+)\/matches\/[^/]+\/(start|score|walkover|live-score)/);
      if (m) {
        const pin = sessionStorage.getItem(`loveall:scoring_pin:${m[1]}`);
        if (pin) config.headers['X-Scoring-Pin'] = pin;
      }
    } catch { /* sessionStorage unavailable — skip */ }
    // Force HTTPS on all request URLs
    if (config.url && config.url.includes('http://')) {
      config.url = config.url.replace('http://', 'https://');
    }
    if (config.baseURL && config.baseURL.includes('http://')) {
      config.baseURL = config.baseURL.replace('http://', 'https://');
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only clear auth and redirect for explicit auth failures on protected actions
      // Don't clear for public page API calls or background fetches
      const url = error.config?.url || '';
      const isAuthEndpoint = url.includes('/auth/me');
      if (isAuthEndpoint) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  exchangeSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  refreshToken: (sessionToken) => api.post('/auth/refresh', sessionToken ? { session_token: sessionToken } : {}),
  sendOtp: (phone) => api.post('/auth/send-otp', { phone }),
  verifyOtp: (phone, code, name) => api.post('/auth/verify-otp', { phone, code, name }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  quickDemo: () => api.post('/quick-demo'),
  adminLogin: (data) => api.post('/auth/admin-login', data),
};

// Players API
export const playersAPI = {
  getAll: () => api.get('/players'),
  getById: (userId) => api.get(`/players/${userId}`),
  getHeadToHead: (userId, opponentId) => api.get(`/players/${userId}/head-to-head/${opponentId}`),
};

// Clubs API
export const clubsAPI = {
  create: (data) => api.post('/clubs', data),
  getAll: () => api.get('/clubs'),
  getById: (clubId) => api.get(`/clubs/${clubId}`),
  addMember: (clubId, memberId) => api.post(`/clubs/${clubId}/members/${memberId}`),
  createPair: (data) => api.post('/clubs/pairs', data),
  getPairs: () => api.get('/clubs/pairs/list'),
  assignAdmin: (clubId, adminId) => api.put(`/clubs/${clubId}/admin?admin_id=${adminId}`),
  addUmpire: (clubId, userId) => api.post(`/clubs/${clubId}/umpires/${userId}`),
  getLeaderboard: (clubId) => api.get(`/clubs/${clubId}/leaderboard`),
  getMembersList: (clubId) => api.get(`/clubs/${clubId}/members-list`),
};

// Matches API
export const matchesAPI = {
  create: (data) => api.post('/matches', data),
  getAll: (status) => api.get('/matches', { params: { status } }),
  getById: (matchId) => api.get(`/matches/${matchId}`),
  start: (matchId) => api.post(`/matches/${matchId}/start`),
  updateScore: (matchId, data) => api.post(`/matches/${matchId}/score`, data),
  confirm: (matchId) => api.post(`/matches/${matchId}/confirm`),
  decline: (matchId) => api.post(`/matches/${matchId}/decline`),
};

// Tournaments API
export const analyticsAPI = {
  overview: (days = 30) => api.get(`/analytics/overview?days=${days}`),
};

export const tournamentsAPI = {
  create: (data) => api.post('/tournaments', data),
  getAll: () => api.get('/tournaments'),
  getById: (tournamentId) => api.get(`/tournaments/${tournamentId}`),
  register: (tournamentId) => api.post(`/tournaments/${tournamentId}/register`),
  addTeam: (tournamentId, data) => api.post(`/tournaments/${tournamentId}/teams`, data),
  removeTeam: (tournamentId, teamId) => api.delete(`/tournaments/${tournamentId}/teams/${teamId}`),
  updateTeam: (tournamentId, teamId, data) => api.put(`/tournaments/${tournamentId}/teams/${teamId}`, data),
  createPools: (tournamentId, data) => api.post(`/tournaments/${tournamentId}/pools`, data),
  getMatches: (tournamentId) => api.get(`/tournaments/${tournamentId}/matches`),
  startMatch: (tournamentId, matchId, data) => api.post(`/tournaments/${tournamentId}/matches/${matchId}/start`, data),
  scoreMatch: (tournamentId, matchId, data) => api.post(`/tournaments/${tournamentId}/matches/${matchId}/score`, data),
  walkover: (tournamentId, matchId, data) => api.post(`/tournaments/${tournamentId}/matches/${matchId}/walkover`, data),
  liveScore: (tournamentId, matchId, data) => api.post(`/tournaments/${tournamentId}/matches/${matchId}/live-score`, data),
  shuffleMatches: (tournamentId) => api.post(`/tournaments/${tournamentId}/matches/shuffle`),
  getPointsTable: (tournamentId) => api.get(`/tournaments/${tournamentId}/points-table`),
  generateKnockout: (tournamentId, data) => api.post(`/tournaments/${tournamentId}/generate-knockout`, data),
  generateNextRound: (tournamentId, data) => api.post(`/tournaments/${tournamentId}/generate-next-round`, data),
  assignAdmin: (tournamentId, adminId) => api.put(`/tournaments/${tournamentId}/admin?admin_id=${adminId}`),
  // — Umpire roster —
  listUmpires: (tournamentId) => api.get(`/tournaments/${tournamentId}/umpires`),
  addUmpire: (tournamentId, data) => api.post(`/tournaments/${tournamentId}/umpires`, data),
  removeUmpire: (tournamentId, umpireId) => api.delete(`/tournaments/${tournamentId}/umpires/${umpireId}`),
  // — Multi-round pools / Super 8 —
  generateSuperPools: (tournamentId, data) => api.post(`/tournaments/${tournamentId}/super-pools`, data),
  // — Direct Knockout (no pools) —
  generateDirectKnockout: (tournamentId, data = {}) => api.post(`/tournaments/${tournamentId}/direct-knockout`, data),
  // — Player's tournaments —
  mine: () => api.get(`/tournaments/mine/by-email`),
  // — Invite players via email —
  invite: (tournamentId, data = {}) => api.post(`/tournaments/${tournamentId}/invite`, data),
  // — Tournament expansion (e.g. 8 → 16) —
  patchTournament: (tournamentId, data) => api.patch(`/tournaments/${tournamentId}`, data),
  expand: (tournamentId, data) => api.post(`/tournaments/${tournamentId}/expand`, data),
  // — Daily scoring PIN (organizer-only) —
  getScoringPin: (tournamentId) => api.get(`/tournaments/${tournamentId}/scoring-pin`),
  // — Featured tournament for the public landing page —
  getFeatured: () => api.get('/tournaments/featured'),
  setFeatured: (tournamentId, data) => api.put(`/tournaments/${tournamentId}/featured`, data),
  // — Anonymous WhatsApp number capture —
  subscribe: (tournamentId, data) => api.post(`/tournaments/${tournamentId}/subscribe`, data),
  // — Standings text card for WhatsApp share —
  standingsBroadcast: (tournamentId) => api.get(`/tournaments/${tournamentId}/standings-broadcast`),
};

// Public API (no auth)
export const publicAPI = {
  getTournament: (tournamentId) => api.get(`/public/tournaments/${tournamentId}`),
  getTournamentMatches: (tournamentId) => api.get(`/public/tournaments/${tournamentId}/matches`),
  getPointsTable: (tournamentId) => api.get(`/public/tournaments/${tournamentId}/points-table`),
  getMatch: (matchId) => api.get(`/public/matches/${matchId}`),
};

// Rankings API
export const rankingsAPI = {
  getAll: (category = 'overall') => api.get('/rankings', { params: { category } }),
  getAwards: () => api.get('/rankings/awards'),
};

// Venues API
export const venuesAPI = {
  create: (data) => api.post('/venues', data),
  getAll: () => api.get('/venues'),
  getById: (venueId) => api.get(`/venues/${venueId}`),
  getMatches: (venueId) => api.get(`/venues/${venueId}/matches`),
  assignAdmin: (venueId, adminId) => api.put(`/venues/${venueId}/admin?admin_id=${adminId}`),
  createBooking: (venueId, data) => api.post(`/venues/${venueId}/bookings`, null, { params: data }),
  getBookings: (venueId) => api.get(`/venues/${venueId}/bookings`),
  payBooking: (bookingId, method) => api.post(`/venues/bookings/${bookingId}/pay?payment_method=${method}`),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  seedDemoData: () => api.post('/admin/seed-demo-data'),
  getAllUsers: () => api.get('/admin/users'),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, null, { params: { role } }),
  createPlayer: (data) => api.post('/admin/create-player', null, { params: data }),
  getRoles: () => api.get('/admin/roles'),
  getRevenue: () => api.get('/admin/revenue'),
};

// Quick Access API
export const quickAccessAPI = {
  getRoles: () => api.get('/quick-access-roles'),
  loginAs: (role) => api.post(`/quick-access/${role}`),
};

// Memberships API
export const membershipsAPI = {
  // Tiers
  createTier: (clubId, data) => api.post(`/memberships/tiers?club_id=${clubId}`, data),
  getTiers: (clubId) => api.get(`/memberships/tiers/${clubId}`),
  updateTier: (tierId, data) => api.put(`/memberships/tiers/${tierId}`, data),
  deleteTier: (tierId) => api.delete(`/memberships/tiers/${tierId}`),
  toggleTier: (tierId) => api.put(`/memberships/tiers/${tierId}/toggle`),
  
  // Memberships
  create: (data) => api.post('/memberships', data),
  getByClub: (clubId, status) => api.get(`/memberships/club/${clubId}`, { params: { status } }),
  getByUser: (userId) => api.get(`/memberships/user/${userId}`),
  toggleMember: (membershipId) => api.put(`/memberships/members/${membershipId}/toggle`),
  
  // Payments
  recordPayment: (data) => api.post('/memberships/payments', data),
  getPayments: (clubId) => api.get(`/memberships/payments/${clubId}`),
  
  // Reminders
  getReminders: (clubId) => api.get(`/memberships/reminders/${clubId}`),
  sendReminder: (membershipId) => api.post(`/memberships/send-reminder/${membershipId}`),
  
  // Player's own dues
  getMyDues: () => api.get('/memberships/my-dues'),
};

// Support API
export const supportAPI = {
  createTicket: (data) => api.post('/support/tickets', data),
  getTickets: () => api.get('/support/tickets'),
  respond: (ticketId, data) => api.put(`/support/tickets/${ticketId}/respond`, data),
};

// Profile API
export const profileAPI = {
  update: (data) => api.put('/auth/profile', data),
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (notificationId) => api.post(`/notifications/${notificationId}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  delete: (notificationId) => api.delete(`/notifications/${notificationId}`),
};

export const guestAPI = {
  createMatch: (data) => api.post('/guest/matches', data),
  getMatch: (matchId) => api.get(`/guest/matches/${matchId}`),
  startMatch: (matchId) => api.post(`/guest/matches/${matchId}/start`),
  scoreMatch: (matchId, data) => api.post(`/guest/matches/${matchId}/score`, data),
};

export default api;
